import React, { useContext, useMemo, useState, useEffect } from 'react';
import { Typography, Button, Modal, message, Empty, Input, Form, Space, App, Checkbox } from 'antd';
import { DeleteOutlined, WarningOutlined, PlusOutlined, MinusCircleOutlined, ShareAltOutlined } from '@ant-design/icons';
import AppContext from '../context/AppContext';

const { Title, Text } = Typography;

const CustomListModal = ({ visible, onCancel, onSubmit, editingList }) => {
  const [form] = Form.useForm();
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (visible) {
      if (editingList) {
        form.setFieldsValue(editingList);
      } else {
        form.resetFields();
      }
      setIsValid(false);
    }
  }, [visible, form, editingList]);

  const handleFormChange = () => {
    const values = form.getFieldsValue();
    const isNameValid = values.name?.trim();
    const areItemsValid = values.items?.length > 0 && 
      values.items.every(item => item?.name?.trim() && item?.measure?.trim());
    
    setIsValid(isNameValid && areItemsValid);
  };

  const handleSubmit = () => {
    form.validateFields().then(values => {
      // Validate that there's at least one item
      if (!values.items || values.items.length === 0) {
        message.error('Please add at least one item');
        return;
      }

      // Validate that no fields are empty or just whitespace
      const hasEmptyFields = values.items.some(
        item => !item.name.trim() || !item.measure.trim()
      );
      if (hasEmptyFields) {
        message.error('Please fill in all fields');
        return;
      }

      onSubmit({
        ...values,
        name: values.name.trim(),
        items: values.items.map(item => ({
          name: item.name.trim(),
          measure: item.measure.trim()
        }))
      });
      form.resetFields();
    });
  };

  return (
    <Modal
      title={editingList ? "Edit Custom List" : "Create Custom List"}
      open={visible}
      onCancel={() => {
        form.resetFields();
        setIsValid(false);
        onCancel();
      }}
      onOk={handleSubmit}
      okText={editingList ? "Save" : "Create"}
      okButtonProps={{ disabled: !isValid }}
      styles={{
        body: {
          padding: '24px'
        }
      }}
    >
      <Form 
        form={form} 
        layout="vertical"
        initialValues={{
          items: [{ name: '', measure: '' }]
        }}
        onValuesChange={handleFormChange}
      >
        <Form.Item
          name="name"
          label="List Name"
          rules={[
            { required: true, message: 'Please enter a list name' },
            { whitespace: true, message: 'List name cannot be empty' },
            { min: 2, message: 'List name must be at least 2 characters' },
            { max: 50, message: 'List name cannot exceed 50 characters' }
          ]}
        >
          <Input 
            placeholder="Enter list name"
            maxLength={50}
          />
        </Form.Item>

        <Form.List
          name="items"
          rules={[
            {
              validator: async (_, items) => {
                if (!items || items.length === 0) {
                  return Promise.reject(new Error('Please add at least one item'));
                }
              },
            },
          ]}
        >
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                  <Form.Item
                    {...restField}
                    name={[name, 'name']}
                    rules={[
                      { required: true, message: 'Missing ingredient/item' },
                      { whitespace: true, message: 'Ingredient/item name cannot be empty' },
                      { min: 2, message: 'Ingredient/item name must be at least 2 characters' },
                      { max: 50, message: 'Ingredient/item name cannot exceed 50 characters' }
                    ]}
                  >
                    <Input 
                      placeholder="Ingredient/Item"
                      maxLength={50}
                    />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'measure']}
                    rules={[
                      { required: true, message: 'Missing amount' },
                      { whitespace: true, message: 'Amount cannot be empty' },
                      { max: 20, message: 'Amount cannot exceed 20 characters' }
                    ]}
                  >
                    <Input 
                      placeholder="Amount"
                      maxLength={20}
                    />
                  </Form.Item>
                  {fields.length > 1 && (
                    <MinusCircleOutlined 
                      onClick={() => remove(name)}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                </Space>
              ))}
              <Form.Item>
                <Button 
                  type="dashed" 
                  onClick={() => add()} 
                  block 
                  icon={<PlusOutlined />}
                  disabled={fields.length >= 20}  // Limit number of items
                >
                  Add Ingredient
                </Button>
                {fields.length >= 20 && (
                  <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                    Maximum 20 items allowed
                  </div>
                )}
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

const ShoppingList = () => {
  const { state, dispatch } = useContext(AppContext);
  const [customListModalVisible, setCustomListModalVisible] = useState(false);
  const [editingList, setEditingList] = useState(null);

  // Find duplicate items
  const { duplicates, groupedItems } = useMemo(() => {
    const itemCounts = {};
    const duplicateItems = new Set();

    // Count occurrences of each item
    state.shoppingList.forEach(item => {
      const key = `${item.name}-${item.measure}`;
      if (itemCounts[key]) {
        duplicateItems.add(key);
        itemCounts[key]++;
      } else {
        itemCounts[key] = 1;
      }
    });

    // Group items by meal
    const grouped = state.shoppingList.reduce((acc, item) => {
      if (!acc[item.mealName]) {
        acc[item.mealName] = [];
      }
      acc[item.mealName].push({
        ...item,
        isDuplicate: duplicateItems.has(`${item.name}-${item.measure}`)
      });
      return acc;
    }, {});

    return {
      duplicates: duplicateItems,
      groupedItems: grouped
    };
  }, [state.shoppingList]);

  const handleRemoveItem = (itemId) => {
    dispatch({
      type: 'REMOVE_ITEM',
      payload: itemId
    });
  };

  const handleTogglePurchased = (itemId, listType = 'regular') => {
    dispatch({
      type: 'TOGGLE_PURCHASED',
      payload: itemId,
      listType: listType
    });
  };

  const handleClearList = () => {
    Modal.confirm({
      title: 'Clear All Lists',
      content: 'Are you sure you want to clear all shopping lists? This will remove both recipe items and custom lists.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        dispatch({
          type: 'CLEAR_ALL_LISTS'
        });
        message.success('All lists cleared');
      }
    });
  };

  const handleRemoveDuplicates = () => {
    dispatch({
      type: 'REMOVE_DUPLICATES'
    });
    message.success('Duplicate items removed');
  };

  const handleEditList = (list) => {
    setEditingList(list);
    setCustomListModalVisible(true);
  };

  const handleRemoveCustomList = (listId) => {
    Modal.confirm({
      title: 'Remove Custom List',
      content: 'Are you sure you want to remove this list?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        dispatch({
          type: 'REMOVE_CUSTOM_LIST',
          payload: listId
        });
        message.success('Custom list removed');
      }
    });
  };

  const handleRemoveCustomListItem = (listId, itemId) => {
    dispatch({
      type: 'REMOVE_CUSTOM_LIST_ITEM',
      payload: { listId, itemId }
    });
  };

  const handleCustomListSubmit = (values) => {
    if (editingList) {
      dispatch({
        type: 'EDIT_CUSTOM_LIST',
        payload: {
          ...values,
          id: editingList.id
        }
      });
      message.success('Custom list updated');
    } else {
      dispatch({
        type: 'ADD_CUSTOM_LIST',
        payload: values
      });
      message.success('Custom list created');
    }
    setCustomListModalVisible(false);
    setEditingList(null);
  };

  const handleShareList = async () => {
    const listContent = state.shoppingList.map(item => `${item.name} - ${item.measure}`).join('\n');
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Shopping List',
          text: listContent,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          navigator.clipboard.writeText(listContent);
          message.success('Shopping list copied to clipboard');
        }
      }
    } else {
      navigator.clipboard.writeText(listContent);
      message.success('Shopping list copied to clipboard');
    }
  };

  const renderHeader = () => (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      marginBottom: '16px'
    }}>
      <Title level={4} style={{ margin: 0 }}>Shopping List</Title>
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCustomListModalVisible(true)}
        >
          Create List
        </Button>
        {duplicates.size > 0 && (
          <Button 
            type="primary"
            danger
            onClick={handleRemoveDuplicates}
            icon={<WarningOutlined />}
            style={{ 
              position: 'fixed',
              bottom: '1rem',
              zIndex: 1,
            }}
          >
            Remove Duplicates?
          </Button>
        )}
        <Button 
            type="primary"
            onClick={handleShareList}
            icon={<ShareAltOutlined />}
            style={{ 
              position: 'fixed',
              bottom: '1rem',
              zIndex: 1,
              right: '1rem'
            }}
          >
          </Button>

        {(state.shoppingList.length > 0 || state.customLists?.length > 0) && (
          <Button
            type="primary" 
            danger
            onClick={handleClearList}
          >
            Clear List
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <App>
      <div style={{ padding: '16px' }}>
        {renderHeader()}

        {/* Recipe-based items */}
        {state.shoppingList.length === 0 && (!state.customLists || state.customLists.length === 0) ? (
          <Empty description="Your shopping list is empty" />
        ) : (
          <>
            {/* Recipe-based items */}
            {state.shoppingList.length > 0 && Object.entries(groupedItems).map(([mealName, items]) => (
              <div key={mealName} style={{ marginBottom: '24px' }}>
                <Title level={5}>{mealName}</Title>
                {items.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px',
                      backgroundColor: item.isDuplicate ? '#fff1f0' : 'transparent',
                      borderRadius: '4px',
                      marginBottom: '8px'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px' 
                    }}>
                      <Checkbox
                        checked={item.purchased}
                        onChange={() => handleTogglePurchased(item.id)}
                        style={{ 
                          transform: 'scale(1.3)',
                          marginRight: '4px'
                        }}
                      />
                      <div style={{ 
                        textDecoration: item.purchased ? 'line-through' : 'none',
                        opacity: item.purchased ? 0.5 : 1
                      }}>
                        <Text strong>{item.name}</Text>
                        <br />
                        <Text type="secondary">{item.measure}</Text>
                      </div>
                    </div>
                    <Button 
                      type="text" 
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveItem(item.id)}
                    />
                  </div>
                ))}
              </div>
            ))}

            {/* Custom Lists */}
            {state.customLists?.map((list) => (
              <div key={list.id} style={{ marginBottom: '24px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <Title level={5} style={{ margin: 0 }}>{list.name}</Title>
                  <div style={{ 
                    display: 'flex', 
                    gap: '8px',
                    alignItems: 'center',
                    paddingRight: '0.5rem'
                  }}>
                    <Button
                      type="primary"
                      ghost
                      size="small"
                      style={{ 
                        fontSize: '12px', 
                        padding: '0 8px',
                        height: '24px',
                        lineHeight: '22px'
                      }}
                      onClick={() => handleEditList(list)}
                    >
                      EDIT
                    </Button>
                    <Button
                      type="text" 
                      danger
                      style={{ padding: '4px 8px', height: 'auto' }}
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveCustomList(list.id)}
                    />
                  </div>
                </div>
                {list.items.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px',
                      borderRadius: '4px',
                      marginBottom: '8px'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px' 
                    }}>
                      <Checkbox
                        checked={item.purchased}
                        onChange={() => handleTogglePurchased(item.id, 'custom')}
                        style={{ 
                          transform: 'scale(1.3)',
                          marginRight: '4px'
                        }}
                      />
                      <div style={{ 
                        textDecoration: item.purchased ? 'line-through' : 'none',
                        opacity: item.purchased ? 0.5 : 1
                      }}>
                        <Text strong>{item.name}</Text>
                        <br />
                        <Text type="secondary">{item.measure}</Text>
                      </div>
                    </div>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveCustomListItem(list.id, item.id)}
                    />
                  </div>
                ))}
              </div>
            ))}
          </>
        )}

        <CustomListModal
          visible={customListModalVisible}
          onCancel={() => {
            setCustomListModalVisible(false);
            setEditingList(null);
          }}
          onSubmit={handleCustomListSubmit}
          editingList={editingList}
        />
      </div>
    </App>
  );
};

export default ShoppingList; 